import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  FilterChipDto,
  GetLageriTableQuery,
  IGetLageriTableQuery,
  LageriClient,
  LagerTableDto,
  PaginatedResultOfLagerTableDtoAndLagerTableSummary,
} from '@kodit/core/data-api';
import {
  ConfigService,
  DocumentPreviewService,
  ensureFileUrl,
} from '@kodit/core/services';
import { toCamelCase } from '@kodit/core/shared';
import { LoaderService } from 'libs/core/shared-ui/src/lib/loader/loader.service';
import {
  ActionClass,
  ActionType,
  IPaginatedResultTableDto,
  TableConfig,
} from 'libs/core/shared-ui/src/lib/table/table-common';
import { LazyLoadEvent } from 'primeng/api';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LagerFilterComponent } from '../lager-filter/lager-filter.component';
import { LagerFilterService } from '../lager-filter/lager-filter.service';
import { Store } from '@ngrx/store';
import { selectFilterByKey } from '../../racun/state/filters/filter.selectors';
import { setFilter } from '../../racun/state/filters/filter.actions';

/* !! nazivi polja moraju da se poklapaju sa LagerSortField na backendu */
const LAGER_SORT_FIELD_NAZIV = 'naziv';
const LAGER_SORT_FIELD_SIFRA = 'sifra';

@Component({
  selector: 'kodit-lager-table',
  templateUrl: './lager-table.component.html',
  styleUrls: ['./lager-table.component.scss'],
})
export class LagerTableComponent implements OnInit, OnDestroy {
  /** Subs */
  private _tableRefreshSub: Subscription = new Subscription();
  private _deleteSub: Subscription = new Subscription();
  private _dialogCloseSub: Subscription = new Subscription();
  private _subs: Subscription = new Subscription();

  /** Props */
  paginatedData: IPaginatedResultTableDto;
  lagerTable: LagerTableDto[];
  chipItems: FilterChipDto[] = [];
  filters: GetLageriTableQuery;
  isFirstLoad = true;
  routerChangeDetected: boolean;
  isLoading = true;

  /** Confings */
  tableConfig: TableConfig;
  skipFirst: number = 0;
  numberOfRowsDisplayed: number = 0;

  @Input() showCardBlock: boolean = true;
  @Input() isFromMagacinInfo: boolean = false;
  @Input() magacinId: number = null;

  constructor(
    private _client: LageriClient,
    private _router: Router,
    private _filterService: LagerFilterService,
    private _loaderService: LoaderService,
    private _configService: ConfigService,
    private _storage: Store,
    private _docPreviewService: DocumentPreviewService
  ) {}

  ngOnInit(): void {
    //check if page is reloaded
    if (this._router.onSameUrlNavigation == 'reload') {
      this.routerChangeDetected = true;
    }

    this._subs.add(
      this._storage.select(selectFilterByKey('LAGERI')).subscribe((data) => {
        if (data) {
          this.filters = JSON.parse(data);
          this.skipFirst = this.filters.pageNumber ?? 0;
          this.numberOfRowsDisplayed = this.filters.pageSize ?? 0;
        } else {
          this.filters = new GetLageriTableQuery({
            prikaziNuleIMinus: true
          });
        }
        this._load();
      })
    );
  }

  ongMagacinChange(id: number) {
    this.filters.magacinId = id;
    this._updateStorage();
  }

  async setTableConfig(isMaloprodaja: boolean): Promise<void> {
    this.tableConfig = new TableConfig({
      tableHeader: 'Lager',
      isLazy: true,
      initialSortField: this.filters?.sortField ?? LAGER_SORT_FIELD_NAZIV,
      initialSortOrder: this.filters?.sortOrder ?? 1,
      lazyCallbackFunction: (event: LazyLoadEvent) => {
        if (!this.isFirstLoad) {
          this.skipFirst = event.first;
          this.numberOfRowsDisplayed = event.rows;
        }
        this._updateFilterDataAndReload(event.sortField, event.sortOrder);
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
        }
      },
      onChipRemove: (chips: FilterChipDto) => this._handleChipRemove(chips),
      columns: [
        {
          field: LAGER_SORT_FIELD_SIFRA,
          header: 'Šifra',
          type: 'text',
          sortable: true,
        },
        {
          field: LAGER_SORT_FIELD_NAZIV,
          header: 'Naziv',
          type: 'link',
          sortable: true,
          linkCallbackFunction: (index: number) => this._onInfo(index),
        },
        { field: 'stanje', header: 'Zalihe', type: 'number' },
        {
          field: 'rezervisano',
          header: 'Rezervisano',
          type: 'number',
        },
        {
          field: 'slobodno',
          header: 'Raspoloživo',
          type: 'number',
        },
        {
          field: 'cenaDecimal',
          header: isMaloprodaja
            ? 'Prodajna cena sa PDV-om'
            : 'Pros. nabavna cena',
          type: 'currency',
          currencyAlphaCharField: 'valutaText',
        },
        {
          field: 'vrednostDecimal',
          header: 'Vrednost',
          type: 'currency',
          currencyAlphaCharField: 'valutaText',
        },
      ],
      headerActions: [
        {
          type: ActionType.CUSTOM,
          actionClass: ActionClass.OUTLINED,
          icon: 'fa-regular fa-print',
          hasAccessTooltip: 'Štampanje lagera',
          callback: (index: number) => {
            const lager = this.lagerTable[index - this.skipFirst];
            const filter: IGetLageriTableQuery = this.tableConfig.advancedFilter
              .data;

            this._docPreviewService.openDialog(
              `lageri/get-lager-pdf/${ensureFileUrl(lager.naziv)}`,
              {
                naziv: filter.naziv,
                sifra: filter.sifra,
                magacinId: filter.magacinId,
                predmetStavkeId: filter.predmetStavkeId,
                datumPrometa: filter.datumPrometa,
                strankaIdList: filter.stranke?.map((s) => s.id),
                pageNumber: filter.pageNumber,
                pageSize: filter.pageSize,
                prikaziNuleIMinus: filter.prikaziNuleIMinus,
                sortField: this.filters.sortField,
                sortOrder: this.filters.sortOrder,
              }
            );
          },
        },
      ],
      advancedFilter: {
        component: LagerFilterComponent,
        data: new GetLageriTableQuery(),
      },
    });
  }

  private _onInfo(index: number): void {
    this._router
      .navigate([
        '/kompanija',
        'roba',
        this.lagerTable[index - this.skipFirst].predmetStavkeId,
      ])
      .then();
  }

  private _handleChipRemove(removedChip: FilterChipDto) {
    this.tableConfig.advancedFilter.data[toCamelCase(removedChip.key)] =
      removedChip.defaultValue;
    this._updateStorage();
  }

  private _updateFilterDataAndReload(sortField?: string, sortOrder?: number) {
    const sortChanged =
      sortField !== undefined &&
      (sortField !== this.filters.sortField ||
        sortOrder !== this.filters.sortOrder);

    if (
      (this.skipFirst !== this.filters.pageNumber ||
        this.numberOfRowsDisplayed !== this.filters.pageSize ||
        sortChanged) &&
      !this.isFirstLoad
    ) {
      this.filters.pageNumber = this.skipFirst;
      this.filters.pageSize = this.numberOfRowsDisplayed;
      if (sortChanged) {
        this.filters.sortField = sortField;
        this.filters.sortOrder = sortOrder;
      }
      this._updateStorage();
    }
  }

  private _updateStorage() {
    this._storage.dispatch(
      setFilter({
        key: 'LAGERI',
        filter: JSON.stringify(this.filters),
      })
    );
  }

  private _load() {
    if (!this.tableConfig) {
      this.setTableConfig(false);
    }

    if (this.magacinId != null) {
      this.filters.magacinId = this.magacinId;
    }

    if (!this.filters.magacinId) {
      return;
    }

    this._configService.setIsBusy = true;
    this.isLoading = true;

    this._subs.add(
      this._client
        .getForTable(this.filters as GetLageriTableQuery)
        .pipe(
          finalize(() => {
            this._configService.setIsBusy = false;
            this.isLoading = false;
          })
        )
        .subscribe(
          (res: PaginatedResultOfLagerTableDtoAndLagerTableSummary) => {
            const cenaCol = this.tableConfig.columns.find(
              (c) => c.field === 'cenaDecimal'
            );
            if (cenaCol) {
              cenaCol.header = res.summaryData.isMaloprodaja
                ? 'Prodajna cena sa PDV-om'
                : 'Pros. nabavna cena';
            }
            this.paginatedData = res;
            this.chipItems = res.activeFilters;
            this.lagerTable = res.data;
            this.tableConfig.advancedFilter.data = this.filters;
          }
        )
    );
  }

  ngOnDestroy(): void {
    this._tableRefreshSub.unsubscribe();
    this._deleteSub.unsubscribe();
    this._dialogCloseSub.unsubscribe();
    this._subs.unsubscribe();
  }
}
